import { useState, useMemo } from 'react'

import { DefaultOptionType as TreeDataNode } from 'antd/es/select'
import { startCase } from 'lodash-es'
import {
  Cancel as CancelIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  UploadFile as UploadFileIcon,
  InfoOutlined
} from '@mui/icons-material'

import { IExportStatus } from '$/types'
import { useGetEventTablesForImportQuery } from 'app/services/api/datamanagement/eventTableAttributes'
import {
  useGetRecentImportsQuery,
  useAddImportDataFilesMutation,
  useUpdateImportCommitStateMutation
} from 'app/services/api/datamanagement/recentImport'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import LoadingSpinner from 'common/components/LoadingSpinner'
import RecentImportsTable from 'features/DataManagement/Components/Tables/RecentImportsTable'

import { useRefetchJobQueue } from './utils/refetchJobQueue'
import ImportDetailSlideout from './ImportDetailSlideout'

export type EventTableOptionsType = Omit<TreeDataNode, 'label'>

// * Download Template Files has been removed temporarily. Import when needed
const DataImport = () => {
  const [fileImportList, setFileImportList] = useState<File[]>([])
  const [dragActive, setDragActive] = useState<boolean>(false)
  const [isSlideoutOpen, setIsSlideoutOpen] = useState<boolean>(false)
  const [importId, setImportId] = useState<number>()

  const [addImportDataFiles] = useAddImportDataFilesMutation()

  const { isFetching: isImportEventTablesLoading } =
    useGetEventTablesForImportQuery()

  const {
    currentData: recentImportTable,
    refetch: refetchImports,
    isFetching: isRecentImportTableLoading
  } = useGetRecentImportsQuery()

  const [updateImportCommitState] = useUpdateImportCommitStateMutation()
  const { queueJobId, isJobQueueActive } = useRefetchJobQueue(refetchImports)

  const createUpdateCommitStatusJob = async (
    commitId: number,
    commitStatus: string
  ): Promise<IExportStatus> => {
    const updateCommitAction = updateImportCommitState({
      csvFileGroupId: commitId,
      commitAction: commitStatus
    }).unwrap()

    queueJobId((await updateCommitAction).jobId)

    return updateCommitAction
  }

  const importFiles = async (): Promise<IExportStatus> => {
    const formData = new FormData()

    fileImportList.map((file) => formData.append('file', file))
    setFileImportList([])

    const fileImportAction = addImportDataFiles(formData).unwrap()

    queueJobId((await fileImportAction).jobId)

    return fileImportAction
  }

  /**
   * Function for adding non-duplicate files to the import list
   *
   * @param newFiles - list of files to upload
   */
  const addFiles = (newFiles: FileList) => {
    const currentFiles = fileImportList

    Array.from(newFiles).forEach((newFile) => {
      if (newFile.type === 'text/csv') {
        let duplicate = false

        currentFiles.forEach((file) => {
          if (file.name === newFile.name) {
            duplicate = true
          }
        })

        if (!duplicate) {
          currentFiles.push(newFile)
        }
      }
    })

    setFileImportList([...currentFiles])
  }

  /**
   * Function for deleting a file from the file import list based on file name
   *
   * @param fileName - name of the file to delete
   */
  const removeFile = (e: React.MouseEvent, fileName: string) => {
    e.preventDefault()

    const newFiles = fileImportList.filter((file) => file.name !== fileName)

    setFileImportList(newFiles)
  }

  /**
   * Event handler to add files to the import list through the file browser
   */
  const handleFileSelect = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement

    if (target.files != null) {
      addFiles(target.files)
    }
  }

  /**
   * Event handler to update UI when dragging files over the import area
   */
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  /**
   * Event handler to add files to the import list through drag and drop
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files != null) {
      addFiles(e.dataTransfer.files)
    }
  }

  const rowDataMemo = useMemo(
    () =>
      (recentImportTable?.dataset1Rows.rows ?? []).map((row) => {
        return {
          id: row.rowEntry.Id,
          importedBy: row.rowEntry.ImportedBy,
          importDate: new Date(row.rowEntry.ImportDate).toLocaleString(
            'en-US',
            {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            }
          ),
          importStatus: row.rowEntry.ImportStatus,
          validationStatus: row.rowEntry.ValidationStatus,
          committedBy: row.rowEntry.CommittedBy,
          commitDate: row.rowEntry.CommitDate,
          eventTables: row.rowEntry.EventTables.split(',').map((str) =>
            startCase(str.split('_')[0])
          )
        }
      }),
    [recentImportTable?.dataset1Rows.rows]
  )

  return (
    <div className='ml-4 mt-4 flex flex-1 gap-8 2xl:ml-16'>
      <div className='mt-4 flex w-128'>
        <OverlayScrollbar scrollStyle='overflow-visible'>
          <div>
            <div
              className={[
                'rounded bg-white px-6 pb-6 pt-3 opacity-50',
                'shadow-upload-file-shadow'
              ].join(' ')}
            >
              <div className='text-[0.875rem] font-semibold leading-4'>
                Upload Files
              </div>
              <input
                data-testid='file-import-input'
                id='file-import'
                type='file'
                accept='.csv'
                onChange={handleFileSelect}
                onClick={(e) => ((e.target as HTMLInputElement).value = '')}
                multiple
                hidden
                disabled
              />
              <label
                htmlFor='file-import'
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div
                  className={[
                    'flex justify-between',
                    'mb-2 mt-4 h-[13.5rem] w-full',
                    'rounded-sm bg-white',
                    `${
                      fileImportList.length === 0
                        ? 'border-2 border-dashed border-lighthouseBlue'
                        : 'border border-gray-300'
                    }`,
                    dragActive && 'bg-lighthouseBlue bg-opacity-20',
                    'hover:cursor-pointer'
                  ].join(' ')}
                >
                  {fileImportList.length === 0 ? (
                    <div className='m-auto text-center'>
                      <div className='text-6xl text-gray-300'>
                        <UploadFileIcon
                          fontSize='inherit'
                          style={{ display: 'block', margin: '0 auto' }}
                        />
                      </div>
                      <div className='mt-4 font-semibold'>
                        Select CSV file(s) to upload
                      </div>
                      <div className='text-sm tracking-[0.01em] text-gray-700'>
                        Click to Browse or drag and drop here
                      </div>
                    </div>
                  ) : (
                    <OverlayScrollbar>
                      <div className='w-full px-4 pt-2'>
                        {fileImportList.map((file) => {
                          return (
                            <div
                              className={[
                                'mr-1 flex w-full justify-between',
                                'border-b border-b-gray-300 py-1.5'
                              ].join(' ')}
                              key={file.name}
                              title={file.name}
                            >
                              <div
                                className={[
                                  'flex items-center whitespace-nowrap',
                                  'w-full overflow-hidden leading-4'
                                ].join(' ')}
                              >
                                <DescriptionOutlinedIcon
                                  className='mr-2 text-gray-500'
                                  sx={{ height: '1.125rem', width: '1.125rem' }}
                                />
                                <div className='my-auto text-sm tracking-[0.01em]'>
                                  {file.name.length > 50
                                    ? `${file.name.substring(0, 50)}...`
                                    : file.name}
                                </div>
                              </div>
                              <div
                                className={[
                                  'ml-4 text-sm leading-4 text-gray-500',
                                  'flex items-center whitespace-nowrap'
                                ].join(' ')}
                              >
                                <button
                                  onClick={(e) => removeFile(e, file.name)}
                                >
                                  <CancelIcon
                                    className='ml-2 text-gray-700'
                                    sx={{
                                      height: '1.125rem',
                                      width: '1.125rem'
                                    }}
                                    fontSize='small'
                                  />
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </OverlayScrollbar>
                  )}
                </div>
              </label>
              <div className='flex items-center justify-between'>
                <div className='text-sm font-medium tracking-[0.01em] text-gray-900'>
                  {fileImportList.length > 0 &&
                    fileImportList.length + ' files ready for import'}
                </div>
                <button
                  className={[
                    'h-10 w-auto px-6 py-3',
                    'rounded bg-lighthouseBlue',
                    'text-[0.875rem] font-medium text-white',
                    'leading-4 tracking-[0.03em] disabled:opacity-30'
                  ].join(' ')}
                  disabled={fileImportList.length < 1}
                  onClick={importFiles}
                >
                  Import Data
                </button>
              </div>
            </div>
            <div className='flex pt-4'>
              <InfoOutlined className='text-blue' fontSize='medium' />
              <div className='pl-4 font-semibold'>
                Data Import is currently unavailable. We are working diligently
                to improve your experience. Thank you for your understanding and
                patience.
              </div>
            </div>
          </div>
        </OverlayScrollbar>
      </div>
      <LoadingSpinner
        isLoading={
          isRecentImportTableLoading ||
          isImportEventTablesLoading ||
          isJobQueueActive
        }
      >
        <div className='flex h-full'>
          <RecentImportsTable
            rowData={rowDataMemo}
            statusCallback={createUpdateCommitStatusJob}
            infoCallback={(id: number) => {
              setIsSlideoutOpen(true)
              setImportId(id)
            }}
          />
        </div>
      </LoadingSpinner>
      <ImportDetailSlideout
        isOpen={isSlideoutOpen}
        closeSlideout={() => setIsSlideoutOpen(false)}
        importId={importId}
      />
    </div>
  )
}

export default DataImport
